import React, { useRef, useMemo } from 'react';
import { Box, Flex, Icon, Image, Avatar } from '@chakra-ui/react';
import { KText, KButton } from '@/ui-library/components';
import { FormBuilder } from '@/components/formBuilder';
import {
  transformSurveyToFormConfig,
  getInitialValuesForSurvey,
} from '@/formConfigs/surveyQuestionsAnswersFormConfig';
import { TruncatedTextWithTooltip } from '@/ui/TooltipText';
import { colors } from '@/theme/foundations/colors';
import { useSelector } from 'react-redux';
import { selectUser } from '@/store/bootstrap/selectors';
import { Clock } from '@kula-ai/icons/dual-tone/Clock';
import { calculateSurveyTime } from '@/utils/surveyTimeEstimation';
import { useQuery } from '@tanstack/react-query';
import { SURVEY_API_SERVICES } from '../api';
import { CenterSpinner } from '@/components/ats';
import { SurveyPreviewWithAnswers } from './SurveyPreviewWithAnswers';

interface SurveyPreviewContentProps {
  customFields: any[];
  fieldGroups?: Record<number, string>;
  surveyType?: string;
  surveyData?: any;
  consumer?: string;
  isPreview?: boolean;
}

export const SurveyPreviewContent: React.FC<SurveyPreviewContentProps> = ({
  customFields = [],
  fieldGroups = {},
  surveyType = 'candidate',
  surveyData,
  consumer,
  isPreview = true,
}) => {
  const formRef = useRef(null);
  const isCandidateSurvey = surveyType === 'candidate';
  const currUser = useSelector(selectUser);
  const accountId = window.location.hostname?.split('.')?.[0];

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { data: themeData, isLoading: isThemeLoading } = useQuery({
    queryKey: ['theme', accountId],
    queryFn: () => SURVEY_API_SERVICES.getBrandingTheme(),
    staleTime: 0,
    enabled: isCandidateSurvey,
  });

  // Calculate survey completion time
  const readingTimeText = useMemo(() => {
    const completionTime = calculateSurveyTime(customFields);
    return completionTime || null;
  }, [customFields]);

  if (isThemeLoading) {
    return (
      <CenterSpinner
        spinnerProps={{ width: '40px', height: '40px' }}
        position="relative"
        bottom="120px"
      />
    );
  }

  const themeDetails = themeData?.data;

  // Group questions by field_group_idx
  const groupedQuestions = customFields.reduce(
    (acc, field) => {
      const groupIdx = field.field_group_idx || 1;
      if (!acc[groupIdx]) {
        acc[groupIdx] = [];
      }
      acc[groupIdx].push(field);
      return acc;
    },
    {} as Record<number, any[]>
  );

  // Get section name from field_groups
  const getSectionName = (groupIdx: number) => {
    return fieldGroups[groupIdx] || `Section ${groupIdx}`;
  };

  // Sort group indices
  const sortedGroupIndices = Object?.keys(groupedQuestions)
    ?.map(Number)
    ?.sort((a, b) => a - b);
  // For candidate surveys: render with border and light background
  if (isCandidateSurvey) {
    return (
      <Flex
        flexDir="column"
        bg={
          themeDetails?.theme?.secondary?.backgroundColor
            ? themeDetails?.theme?.secondary?.backgroundColor
            : 'secondary-25'
        }
        p="40px"
        minH="500px"
        alignItems="center"
      >
        {themeDetails?.logoUrl && (
          <Image
            src={themeDetails?.logoUrl}
            width="120px"
            height="50px"
            objectFit="contain"
            mb="20px"
          />
        )}
        {/* Survey Header Box */}
        <Flex
          alignItems="center"
          minW="800px"
          maxW="800px"
          mx="auto"
          bg="base-white"
          px="36px"
          py="36px"
          borderRadius="6px"
          border="1px solid"
          borderColor="border-color"
          mb="12px"
          flexDir="column"
          gap="12px"
        >
          {/* Survey Name and Time */}
          <Flex w="100%" justifyContent="space-between" alignItems="center">
            <KText size="lg-medium" color="secondary-900">
              {surveyData?.title || 'Survey'}
            </KText>
            {readingTimeText && (
              <Flex alignItems="center" gap="6px">
                <Icon
                  as={Clock}
                  width="16px"
                  height="16px"
                  color="secondary-600"
                />
                <KText size="sm-medium" color="secondary-600">
                  {readingTimeText}
                </KText>
              </Flex>
            )}
          </Flex>
          <Box
            w="100%"
            borderBottom="1px solid"
            borderColor="border-color"
            my="4px"
          />
          {/* Sent by and Job */}
          <Flex w="100%" gap="24px" alignItems="center">
            <Flex alignItems="center" gap="6px">
              <KText size="sm-medium" color="secondary-600">
                Sent by
              </KText>
              <Avatar
                size="xs"
                name={currUser?.first_name + ' ' + currUser?.last_name}
                src={currUser?.image_url}
                borderRadius="sm"
              />
              <KText size="sm-medium" color="secondary-900">
                {currUser?.first_name} {currUser?.last_name}
              </KText>
            </Flex>
            <Flex alignItems="center" gap="6px">
              <KText size="sm-medium" color="secondary-600">
                Job
              </KText>
              <KText size="sm-medium" color="secondary-900">
                Senior software engineer
              </KText>
            </Flex>
          </Flex>
        </Flex>

        {/* Questions Box */}
        {sortedGroupIndices.length > 0 ? (
          <Flex
            maxW="800px"
            mx="auto"
            w="100%"
            bg="base-white"
            border="1px solid"
            borderColor="border-color"
            borderRadius="sm"
            flexDir="column"
          >
            {/* Questions Content */}
            <Box p="24px">
              {sortedGroupIndices.map((groupIdx, index) => {
                const sectionQuestions = groupedQuestions[groupIdx];
                const sectionName = getSectionName(groupIdx);
                const formData = transformSurveyToFormConfig(
                  sectionQuestions,
                  isPreview
                );
                const initialValues =
                  getInitialValuesForSurvey(sectionQuestions);

                // Check if section contains NPS questions
                const hasNPSQuestion = sectionQuestions?.some(
                  (q) => q.kind === 'nps_rating'
                );

                return (
                  <Box key={groupIdx} mt={index > 0 ? '20px' : '0'}>
                    {/* Section Header */}
                    <Flex alignItems="center" gap="12px">
                      <Flex
                        alignItems="center"
                        gap="6px"
                        px="8px"
                        py="6px"
                        borderRadius="6px"
                        backgroundColor={colors['secondary-50']}
                      >
                        <TruncatedTextWithTooltip
                          color={'secondary-900'}
                          size="sm-medium"
                          text={sectionName}
                          enableTooltip={true}
                          maxWidth="250px"
                        />
                        {/* Hide count for NPS sections */}
                        {!hasNPSQuestion && (
                          <Flex
                            alignItems="center"
                            bg="base-white"
                            px="6px"
                            borderRadius="6px"
                            border="1px solid"
                            borderColor="border-color"
                          >
                            <KText
                              as="span"
                              size="sm-semi-bold"
                              color="secondary-700"
                            >
                              {sectionQuestions?.length}
                            </KText>
                          </Flex>
                        )}
                      </Flex>
                      <Box
                        borderBottom="1px solid"
                        borderColor="border-color"
                        flex={1}
                      />
                    </Flex>

                    {/* Section Questions */}
                    <Box>
                      <FormBuilder
                        config={formData}
                        formRef={formRef}
                        initialValues={initialValues}
                      />
                    </Box>
                  </Box>
                );
              })}
            </Box>

            {/* Footer Buttons */}
            <Flex
              gap="6px"
              justifyContent="flex-end"
              bg="base-white"
              borderTop="1px solid"
              borderTopColor="border-color"
              p="16px"
              borderBottomLeftRadius="sm"
              borderBottomRightRadius="sm"
            >
              <KButton
                variant="primary"
                bg={
                  themeDetails?.theme?.primary?.backgroundColor
                    ? themeDetails?.theme?.primary?.backgroundColor
                    : 'primary-500'
                }
                color={
                  themeDetails?.theme?.p?.color
                    ? themeDetails?.theme?.p?.color
                    : 'base-white'
                }
                _hover={{
                  bg: themeDetails?.theme?.primary?.backgroundColor
                    ? themeDetails?.theme?.primary?.backgroundColor
                    : 'primary-500',
                  color: themeDetails?.theme?.p?.color
                    ? themeDetails?.theme?.p?.color
                    : 'base-white',
                }}
                _active={{
                  bg: themeDetails?.theme?.primary?.backgroundColor
                    ? themeDetails?.theme?.primary?.backgroundColor
                    : 'primary-500',
                  color: themeDetails?.theme?.p?.color
                    ? themeDetails?.theme?.p?.color
                    : 'base-white',
                }}
              >
                Submit survey
              </KButton>
            </Flex>
          </Flex>
        ) : (
          <Box
            textAlign="center"
            py="48px"
            bg="base-white"
            border="1px solid"
            borderColor="border-color"
            borderRadius="sm"
            maxW="800px"
            mx="auto"
            w="100%"
          >
            <KText size="sm-regular" color="secondary-500">
              No questions added yet
            </KText>
          </Box>
        )}
      </Flex>
    );
  }

  // For employee surveys: render with answer tags for job context, otherwise interactive form
  if (consumer === 'job') {
    return (
      <SurveyPreviewWithAnswers
        customFields={customFields}
        fieldGroups={fieldGroups}
      />
    );
  }

  return (
    <Flex flexDir="column">
      {/* Questions Content */}
      <Box p="16px">
        {sortedGroupIndices.length > 0 ? (
          <>
            {sortedGroupIndices.map((groupIdx, index) => {
              const sectionQuestions = groupedQuestions[groupIdx];
              const sectionName = getSectionName(groupIdx);
              const formData = transformSurveyToFormConfig(
                sectionQuestions,
                isPreview
              );
              const initialValues = getInitialValuesForSurvey(sectionQuestions);

              // Check if section contains NPS questions
              const hasNPSQuestion = sectionQuestions?.some(
                (q) => q.kind === 'nps_rating'
              );

              return (
                <Box key={groupIdx} mt={index > 0 ? '32px' : '0'}>
                  {/* Section Header */}
                  <Flex alignItems="center" gap="12px">
                    <Flex
                      alignItems="center"
                      gap="6px"
                      px="8px"
                      py="6px"
                      borderRadius="6px"
                      backgroundColor={colors['secondary-50']}
                    >
                      <TruncatedTextWithTooltip
                        color={'secondary-900'}
                        size="sm-medium"
                        text={sectionName}
                        enableTooltip={true}
                        maxWidth="250px"
                      />
                      {/* Hide count for NPS sections */}
                      {!hasNPSQuestion && (
                        <Flex
                          alignItems="center"
                          bg="base-white"
                          px="6px"
                          borderRadius="6px"
                          border="1px solid"
                          borderColor="border-color"
                        >
                          <KText
                            as="span"
                            size="sm-semi-bold"
                            color="secondary-700"
                          >
                            {sectionQuestions?.length}
                          </KText>
                        </Flex>
                      )}
                    </Flex>
                    <Box
                      borderBottom="1px solid"
                      borderColor="border-color"
                      flex={1}
                    />
                  </Flex>

                  {/* Section Questions */}
                  <FormBuilder
                    config={formData}
                    formRef={formRef}
                    initialValues={initialValues}
                  />
                </Box>
              );
            })}
          </>
        ) : (
          <Box textAlign="center" py="48px">
            <KText size="sm-regular" color="secondary-500">
              No questions added yet
            </KText>
          </Box>
        )}
      </Box>
    </Flex>
  );
};
